export const SUITS = ['spades', 'hearts', 'clubs', 'diamonds'] as const;
export const RANKS = ['0', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A', 'W'] as const;
export type Suit = typeof SUITS[number];
export type Rank = typeof RANKS[number];
export type Surface = 'top' | 'side' | 'face';
export type Card = { id: string; suit: Suit; rank: Rank };
export type Placement = Card & { x: number; y: number; orientation: number; by: string; turn: number; era: number; chosenSuit?: Suit; blockedUntil?: number };
export type Player = { id: string; token: string; name: string; bot: boolean; hand: Card[]; uhOh: boolean; seen: string[] };
export type Pending = { rank: 'J' | 'Q' | 'K'; count: number; fromId: string; receivedIds?: string[] };
export type Move = { cardId: string; x: number; y: number; orientation: number; surface: Surface; anchorId: string; chosenSuit?: Suit };
export type LogEntry = {
  id: number; type: string; playerId?: string; fromId?: string; targetId?: string;
  card?: Card; count?: number; text?: string; counteredFromId?: string;
  chosenSuit?: Suit; orientation?: number; rotation?: number; direction?: number; reversed?: boolean;
};
export type State = {
  code: string; version: number; phase: 'lobby' | 'playing' | 'finished' | 'rulesPause';
  hostId: string; players: Player[]; board: Placement[]; deck: Card[]; turnIndex: number;
  direction: number; orientation: number; era: number; turnSerial: number; seed: number;
  pending?: Pending; catchableId?: string; winnerId?: string; pauseReason?: string;
  log: LogEntry[]; nextLogId: number; createdAt: number; expiresAt: number; botDue: number;
};
export type Action = { type: string; version: number; actionId: string; [key: string]: unknown };
export type View = {
  code: string; version: number; phase: State['phase']; hostId: string; youId: string;
  players: { id: string; name: string; bot: boolean; count: number; online: boolean; uhOh: boolean }[];
  hand: Card[]; board: Placement[]; turnId: string; direction: number; orientation: number;
  era: number; turnSerial: number; drawCount: number; legalMoves: Move[]; canDraw: boolean;
  pending?: Pending; canRespondCounter: boolean; canAcceptAttack: boolean;
  catchableId?: string; winnerId?: string; pauseReason?: string; log: LogEntry[];
};

const FACE_RANKS = new Set<Rank>(['J', 'Q', 'K', 'A']);
const VERTICES = [[0, -2], [1, -1], [1, 1], [0, 2], [-1, 1], [-1, -1]];
const SURFACES: Record<Surface, number[]> = { top: [5, 0], side: [1, 2], face: [3, 4] };
const SHIFTS: Record<Surface, number[]> = { top: [0, -2], side: [1, 1], face: [-1, 1] };
const uid = () => crypto.randomUUID();
const numeric = (rank: Rank) => !FACE_RANKS.has(rank) && rank !== 'W';
const copy = <T>(value: T): T => JSON.parse(JSON.stringify(value));

function random(s: State) { s.seed = (Math.imul(s.seed, 1664525) + 1013904223) >>> 0; return s.seed / 4294967296; }
function shuffle(s: State, cards: Card[]) {
  for (let i = cards.length - 1; i > 0; i--) { const j = Math.floor(random(s) * (i + 1)); [cards[i], cards[j]] = [cards[j], cards[i]]; }
  return cards;
}
function record(s: State, type: string, extra: Partial<LogEntry> = {}) {
  s.log.push({ id: s.nextLogId++, type, ...extra });
  if (s.log.length > 45) s.log.splice(0, s.log.length - 45);
}
function cleanName(name: unknown) { return typeof name === 'string' ? name.replace(/[\u0000-\u001f\u007f]/g, '').replace(/\s+/g, ' ').trim().slice(0, 24) : ''; }

export function room(code: string): State {
  const now = Date.now();
  return { code, version: 0, phase: 'lobby', hostId: '', players: [], board: [], deck: [], turnIndex: 0,
    direction: 1, orientation: 0, era: 0, turnSerial: 0, seed: crypto.getRandomValues(new Uint32Array(1))[0],
    log: [], nextLogId: 1, createdAt: now, expiresAt: now + 86400000, botDue: 0 };
}

export function join(s: State, name: string, token: string): Player {
  if (typeof token !== 'string' || token.length < 12 || token.length > 128) throw new Error('Invalid reconnect identity.');
  const returning = s.players.find(p => p.token === token);
  if (returning) return returning;
  if (s.phase !== 'lobby') throw new Error('This match has already started.');
  if (s.players.length >= 5) throw new Error('This table is full.');
  const player: Player = { id: uid(), token, name: cleanName(name) || `Player ${s.players.length + 1}`, bot: false, hand: [], uhOh: false, seen: [] };
  s.players.push(player);
  if (!s.hostId) s.hostId = player.id;
  s.version++;
  s.expiresAt = Date.now() + 86400000;
  record(s, 'join', { playerId: player.id });
  return player;
}

function rotate(x: number, y: number, orientation: number): [number, number] {
  for (let i = 0; i < ((orientation % 3) + 3) % 3; i++) [x, y] = [-(x + y) / 2, (3 * x - y) / 2];
  return [x, y];
}

function triangleKey(x: number, y: number, index: number) {
  const a = VERTICES[index], b = VERTICES[(index + 1) % 6];
  return [`${x},${y}`, `${x + a[0]},${y + a[1]}`, `${x + b[0]},${y + b[1]}`].sort().join('|');
}

function surfaceTriangles(card: Placement, surface: Surface) {
  return SURFACES[surface].map(n => triangleKey(card.x, card.y, (n + card.orientation * 2) % 6));
}

function footprint(x: number, y: number) { return Array.from({ length: 6 }, (_, i) => triangleKey(x, y, i)); }

type VisibleTriangle = { card: Placement; surface: Surface };
function geometry(board: Placement[], era: number) {
  const visible = new Map<string, VisibleTriangle>();
  for (const card of board) if (card.era === era) for (const surface of Object.keys(SURFACES) as Surface[]) {
    for (const triangle of surfaceTriangles(card, surface)) visible.set(triangle, { card, surface });
  }
  const exposed = (card: Placement, surface: Surface) => surfaceTriangles(card, surface).every(t => visible.get(t)?.card.id === card.id);
  return { visible, exposed };
}

function matches(card: Card, base: Placement, surface: Surface) {
  if (surface === 'top') return card.rank === 'W' || card.suit === (base.rank === 'W' ? base.chosenSuit : base.suit);
  if (surface === 'side') return numeric(card.rank) && numeric(base.rank) && card.rank === base.rank;
  return FACE_RANKS.has(card.rank) && card.rank === base.rank;
}

export function legalMovesFor(board: Placement[], card: Card, options: { era: number; orientation: number; turnSerial: number; handCount: number }): Move[] {
  if (card.rank === 'W' && options.handCount <= 1) return [];
  const { visible, exposed } = geometry(board, options.era);
  const moves: Move[] = [], used = new Set<string>();
  for (const anchor of board) {
    if (anchor.era !== options.era) continue;
    for (const surface of Object.keys(SURFACES) as Surface[]) {
      if (!exposed(anchor, surface) || !matches(card, anchor, surface)) continue;
      if (surface === 'top' && (anchor.blockedUntil ?? -1) >= options.turnSerial) continue;
      if (card.rank === 'W' && surface !== 'top') continue;
      const delta = rotate(SHIFTS[surface][0], SHIFTS[surface][1], anchor.orientation);
      const x = anchor.x + delta[0], y = anchor.y + delta[1];
      if (Math.abs(x) > 80 || Math.abs(y) > 160) continue;
      let valid = true;
      const incoming = new Set(footprint(x, y));
      const touched = new Set<string>();
      for (const triangle of footprint(x, y)) {
        const hit = visible.get(triangle);
        if (!hit) continue;
        const key = `${hit.card.id}:${hit.surface}`;
        if (touched.has(key)) continue;
        touched.add(key);
        if (!surfaceTriangles(hit.card, hit.surface).every(t => incoming.has(t))) continue;
        if (!exposed(hit.card, hit.surface) || !matches(card, hit.card, hit.surface)
          || (hit.surface === 'top' && (hit.card.blockedUntil ?? -1) >= options.turnSerial)
          || (card.rank === 'W' && hit.surface !== 'top')) { valid = false; break; }
      }
      if (!valid || !touched.size) continue;
      const orientations = card.rank === 'W' ? [(options.orientation + 1) % 3, (options.orientation + 2) % 3] : [options.orientation];
      for (const orientation of orientations) for (const chosenSuit of card.rank === 'W' ? SUITS : [undefined]) {
        const key = `${x}:${y}:${orientation}:${chosenSuit}`;
        if (!used.has(key)) { moves.push({ cardId: card.id, x, y, orientation, surface, anchorId: anchor.id, ...(chosenSuit ? { chosenSuit } : {}) }); used.add(key); }
      }
    }
  }
  return moves;
}

function counterAllowed(player: Player, pending: Pending) {
  if (pending.rank !== 'Q') return true;
  const own = player.hand.filter(c => !pending.receivedIds?.includes(c.id));
  return own.some(c => c.rank === 'Q') && own.length >= 2;
}

function movesForPlayer(s: State, player: Player) {
  return player.hand.flatMap(card => {
    if (s.pending && (card.rank !== s.pending.rank || !counterAllowed(player, s.pending))) return [];
    if (s.pending?.rank === 'Q' && s.pending.receivedIds?.includes(card.id)) return [];
    return legalMovesFor(s.board, card, { era: s.era, orientation: s.orientation, turnSerial: s.turnSerial, handCount: player.hand.length });
  });
}

export function stagedLegalMoves(v: View, staged: Partial<Move>[]): Move[] {
  if (v.phase !== 'playing' || v.turnId !== v.youId) return [];
  if (!staged.length) return copy(v.legalMoves);
  const board = copy(v.board), hand = copy(v.hand);
  let rank: Rank | undefined;
  for (const step of staged) {
    const card = hand.find(c => c.id === step.cardId);
    if (!card || !numeric(card.rank) || (rank && card.rank !== rank)) return [];
    rank = card.rank;
    const legal = legalMovesFor(board, card, { era: v.era, orientation: v.orientation, turnSerial: v.turnSerial, handCount: hand.length });
    const found = legal.find(m => m.x === step.x && m.y === step.y && m.orientation === (step.orientation ?? v.orientation));
    if (!found) return [];
    board.push({ ...card, x: found.x, y: found.y, orientation: found.orientation, by: v.youId, turn: v.turnSerial, era: v.era,
      ...(card.rank === '0' ? { blockedUntil: v.turnSerial + 1 } : {}) });
    hand.splice(hand.findIndex(c => c.id === card.id), 1);
  }
  if (rank && !v.board.some(c => c.era === v.era && c.rank === rank)) return [];
  return hand.filter(c => !rank || c.rank === rank).flatMap(card => {
    if (v.pending && card.rank !== v.pending.rank) return [];
    return legalMovesFor(board, card, { era: v.era, orientation: v.orientation, turnSerial: v.turnSerial, handCount: hand.length });
  });
}

function recycle(s: State) {
  const later = new Set<string>(), covered = new Set<string>();
  for (let i = s.board.length - 1; i >= 0; i--) {
    const card = s.board[i], triangles = footprint(card.x, card.y);
    if (triangles.some(t => later.has(t))) covered.add(card.id);
    for (const triangle of triangles) later.add(triangle);
  }
  if (!covered.size) return false;
  const collected = s.board.filter(c => covered.has(c.id));
  s.board = s.board.filter(c => !covered.has(c.id));
  s.deck = shuffle(s, collected.map(({ suit, rank }) => ({ id: uid(), suit, rank })));
  record(s, 'recycle', { count: collected.length });
  return true;
}

function drawCards(s: State, player: Player, count: number) {
  let drawn = 0;
  for (let i = 0; i < count; i++) {
    if (!s.deck.length && !recycle(s)) {
      s.phase = 'rulesPause';
      s.pauseReason = 'The draw pile is empty and every remaining card is fully exposed. No covered cards are available to recycle.';
      record(s, 'pause', { text: s.pauseReason });
      break;
    }
    player.hand.push(s.deck.pop()!);
    drawn++;
  }
  if (drawn) player.uhOh = false;
  return drawn;
}

function current(s: State) { return s.players[s.turnIndex]; }
function nextIndex(s: State) { return (s.turnIndex + s.direction + s.players.length) % s.players.length; }
function nextTurn(s: State, extra = false) {
  s.turnSerial++;
  if (!extra) s.turnIndex = nextIndex(s);
  s.botDue = current(s)?.bot ? Date.now() + 1200 : 0;
}

function start(s: State) {
  s.phase = 'playing'; s.deck = shuffle(s, SUITS.flatMap(suit => RANKS.map(rank => ({ id: uid(), suit, rank }))));
  s.board = []; s.orientation = 0; s.era = 0; s.turnSerial = 0; s.direction = 1; s.turnIndex = 0;
  delete s.pending; delete s.catchableId; delete s.winnerId; delete s.pauseReason;
  for (const player of s.players) { player.hand = []; player.uhOh = false; }
  const count = s.players.length === 5 ? 5 : 7;
  for (let i = 0; i < count; i++) for (const player of s.players) player.hand.push(s.deck.pop()!);
  const index = s.deck.findIndex(c => numeric(c.rank) && c.rank !== '0');
  const first = s.deck.splice(index, 1)[0];
  s.board.push({ ...first, x: 0, y: 0, orientation: 0, by: '', turn: -1, era: 0 });
  s.botDue = current(s)?.bot ? Date.now() + 1200 : 0;
  record(s, 'start', { card: first });
}

function apply(s: State, player: Player, action: Action): boolean {
  if (action.type === 'addBot') {
    if (s.phase !== 'lobby' || s.hostId !== player.id || s.players.length >= 5) return false;
    const names = ['Pip', 'Blox', 'Tess', 'Cubie'];
    s.players.push({ id: uid(), token: uid(), name: cleanName(action.name) || names[s.players.filter(p => p.bot).length], bot: true, hand: [], uhOh: false, seen: [] });
    record(s, 'addBot', { playerId: player.id }); return true;
  }
  if (action.type === 'removeBot') {
    const target = s.players.findIndex(p => p.id === action.targetId && p.bot);
    if (s.phase !== 'lobby' || s.hostId !== player.id || target < 0) return false;
    s.players.splice(target, 1); return true;
  }
  if (action.type === 'start' || action.type === 'rematch') {
    if (s.hostId !== player.id || s.players.length < 2 || s.players.length > 5) return false;
    if (action.type === 'start' ? s.phase !== 'lobby' : !['finished', 'rulesPause'].includes(s.phase)) return false;
    start(s); return true;
  }
  if (s.phase !== 'playing') return false;
  if (action.type === 'uhOh') {
    if (player.hand.length !== 1 || player.uhOh) return false;
    player.uhOh = true;
    if (s.catchableId === player.id) delete s.catchableId;
    record(s, 'uhOh', { playerId: player.id }); return true;
  }
  if (action.type === 'catch') {
    const target = s.players.find(p => p.id === s.catchableId && p.id === action.targetId);
    if (!target || target.id === player.id || target.hand.length !== 1 || target.uhOh) return false;
    const count = drawCards(s, target, 3);
    delete s.catchableId; record(s, 'catch', { playerId: player.id, targetId: target.id, count }); return true;
  }
  if (current(s).id !== player.id) return false;
  if (action.type === 'acceptAttack') {
    if (!s.pending) return false;
    const pending = s.pending;
    const count = pending.rank === 'Q' ? pending.count : drawCards(s, player, pending.count);
    delete s.pending; delete s.catchableId;
    record(s, 'acceptAttack', { playerId: player.id, fromId: pending.fromId, targetId: player.id, count, text: pending.rank }); return true;
  }
  if (action.type === 'draw') {
    if (s.pending || movesForPlayer(s, player).length) return false;
    delete s.catchableId;
    const count = drawCards(s, player, 1);
    record(s, 'draw', { playerId: player.id, count });
    if (s.phase === 'playing') nextTurn(s);
    return true;
  }
  if (action.type !== 'play' || !Array.isArray(action.moves) || !action.moves.length || action.moves.length > 4) return false;
  const steps = action.moves as Partial<Move>[];
  const first = player.hand.find(c => c.id === steps[0]?.cardId);
  if (!first) return false;
  if (s.pending && (first.rank !== s.pending.rank || !counterAllowed(player, s.pending))) return false;
  if (steps.length > 1 && (!numeric(first.rank) || !s.board.some(c => c.era === s.era && c.rank === first.rank))) return false;
  const previousPending = s.pending;
  const previousOrientation = s.orientation, previousDirection = s.direction;
  const receivedIds = previousPending?.rank === 'Q' ? previousPending.receivedIds ?? [] : [];
  if (previousPending?.rank === 'Q' && receivedIds.includes(first.id)) return false;
  for (const step of steps) {
    if (!step || typeof step !== 'object') return false;
    const card = player.hand.find(c => c.id === step.cardId);
    if (!card || (steps.length > 1 && card.rank !== first.rank)) return false;
    const legal = legalMovesFor(s.board, card, { era: s.era, orientation: s.orientation, turnSerial: s.turnSerial, handCount: player.hand.length });
    const found = legal.find(m => m.x === step.x && m.y === step.y && m.orientation === (step.orientation ?? s.orientation) && (card.rank !== 'W' || m.chosenSuit === step.chosenSuit));
    if (!found) return false;
    if (card.rank === 'W') { s.era++; s.orientation = found.orientation; }
    s.board.push({ ...card, x: found.x, y: found.y, orientation: found.orientation, by: player.id, turn: s.turnSerial, era: s.era,
      ...(found.chosenSuit ? { chosenSuit: found.chosenSuit } : {}), ...(card.rank === '0' ? { blockedUntil: s.turnSerial + 1 } : {}) });
    player.hand.splice(player.hand.findIndex(c => c.id === card.id), 1);
    record(s, 'play', { playerId: player.id, card });
  }
  delete s.pending; delete s.catchableId;
  if (first.rank === '0' && s.players.length >= 3 && steps.length % 2) s.direction *= -1;
  if (first.rank === 'Q' && player.hand.length) {
    if (!Array.isArray(action.giftIds) || action.giftIds.length !== 1 || typeof action.giftIds[0] !== 'string') return false;
    const giftId = action.giftIds[0];
    if (receivedIds.includes(giftId) || !player.hand.some(c => c.id === giftId)) return false;
    const ids = [...receivedIds, giftId];
    if (ids.some(id => !player.hand.some(c => c.id === id))) return false;
    const receiver = s.players[nextIndex(s)], gifts = player.hand.filter(c => ids.includes(c.id));
    player.hand = player.hand.filter(c => !ids.includes(c.id));
    receiver.hand.push(...gifts); receiver.uhOh = false;
    s.pending = { rank: 'Q', count: (previousPending?.count ?? 0) + 1, fromId: player.id, receivedIds: gifts.map(c => c.id) };
    record(s, 'gift', { playerId: player.id, fromId: player.id, targetId: receiver.id, count: gifts.length });
  } else if (first.rank === 'J' || first.rank === 'K') {
    s.pending = { rank: first.rank, count: (previousPending?.count ?? 0) + (first.rank === 'J' ? 1 : 2), fromId: player.id };
  }
  player.uhOh = player.hand.length === 1 && action.uhOh === true;
  if (player.hand.length === 1) {
    if (player.uhOh) record(s, 'uhOh', { playerId: player.id });
    else s.catchableId = player.id;
  }
  if (!player.hand.length) {
    s.phase = 'finished'; s.winnerId = player.id; delete s.catchableId; delete s.pending;
    record(s, 'win', { playerId: player.id }); return true;
  }
  const effect = { playerId: player.id, fromId: player.id, targetId: s.players[nextIndex(s)].id, card: first };
  if ((first.rank === 'J' || first.rank === 'K') && s.pending) {
    record(s, 'attack', { ...effect, count: s.pending.count,
      ...(previousPending ? { counteredFromId: previousPending.fromId } : {}) });
  } else if (first.rank === '0') {
    record(s, 'block', { ...effect, count: 1, direction: s.direction, reversed: s.direction !== previousDirection });
  } else if (first.rank === 'A') {
    record(s, 'extraTurn', { ...effect, targetId: player.id });
  } else if (first.rank === 'W') {
    record(s, 'wild', { ...effect, chosenSuit: s.board[s.board.length - 1].chosenSuit,
      orientation: s.orientation, rotation: (s.orientation - previousOrientation + 3) % 3 === 1 ? 120 : -120 });
  }
  nextTurn(s, first.rank === 'A');
  return true;
}

export function act(s: State, id: string, input: unknown): boolean {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return false;
  const action = input as Action, player = s.players.find(p => p.id === id);
  if (!player || typeof action.type !== 'string' || typeof action.actionId !== 'string' || !/^[a-zA-Z0-9_.:-]{1,96}$/.test(action.actionId)
    || action.version !== s.version || player.seen.includes(action.actionId)) return false;
  const draft = copy(s), draftPlayer = draft.players.find(p => p.id === id)!;
  if (!apply(draft, draftPlayer, action)) return false;
  draftPlayer.seen.push(action.actionId);
  if (draftPlayer.seen.length > 128) draftPlayer.seen.shift();
  draft.version++; draft.expiresAt = Date.now() + 86400000;
  draft.botDue = draft.phase === 'playing' && current(draft)?.bot ? Math.max(draft.botDue, Date.now() + 700) : 0;
  for (const key of Object.keys(s)) if (!(key in draft)) delete (s as unknown as Record<string, unknown>)[key];
  Object.assign(s, draft);
  return true;
}

export function view(s: State, id: string, online: string[] = []): View {
  const player = s.players.find(p => p.id === id), yourTurn = s.phase === 'playing' && current(s)?.id === id;
  const legalMoves = yourTurn && player ? movesForPlayer(s, player) : [];
  const pending = s.pending ? { ...s.pending } : undefined;
  if (pending && !yourTurn) delete pending.receivedIds;
  return copy({ code: s.code, version: s.version, phase: s.phase, hostId: s.hostId, youId: id,
    players: s.players.map(p => ({ id: p.id, name: p.name, bot: p.bot, count: p.hand.length, online: p.bot || online.includes(p.id), uhOh: p.uhOh })),
    hand: player?.hand ?? [], board: s.board, turnId: current(s)?.id ?? '', direction: s.direction,
    orientation: s.orientation, era: s.era, turnSerial: s.turnSerial, drawCount: s.deck.length, legalMoves,
    canDraw: !!yourTurn && !s.pending && !legalMoves.length, pending, canRespondCounter: !!yourTurn && !!s.pending && !!legalMoves.length,
    canAcceptAttack: !!yourTurn && !!s.pending, catchableId: s.catchableId, winnerId: s.winnerId,
    pauseReason: s.pauseReason, log: s.log });
}

export function tick(s: State, now = Date.now()): boolean {
  if (s.phase !== 'playing' || now < s.botDue) return false;
  const player = current(s);
  if (!player?.bot) return false;
  const base = { version: s.version, actionId: uid() };
  if (s.catchableId && s.catchableId !== player.id) return act(s, player.id, { ...base, type: 'catch', targetId: s.catchableId });
  const visible = view(s, player.id), moves = visible.legalMoves;
  if (s.pending && !moves.length) return act(s, player.id, { ...base, type: 'acceptAttack' });
  if (!moves.length) return act(s, player.id, { ...base, type: 'draw' });
  const rankWeight = (rank: Rank) => rank === 'W' ? -20 : rank === 'A' ? 15 : rank === 'Q' ? 10 : rank === 'J' || rank === 'K' ? 8 : 3;
  const sorted = [...moves].sort((a, b) => rankWeight(player.hand.find(c => c.id === b.cardId)!.rank) - rankWeight(player.hand.find(c => c.id === a.cardId)!.rank));
  let selected = sorted[0];
  const card = player.hand.find(c => c.id === selected.cardId)!;
  if (card.rank === 'W') {
    const suit = SUITS.slice().sort((a, b) => player.hand.filter(c => c.suit === b && c.rank !== 'W').length - player.hand.filter(c => c.suit === a && c.rank !== 'W').length)[0];
    selected = moves.find(m => m.cardId === card.id && m.chosenSuit === suit)!;
  }
  const gift = card.rank === 'Q' ? player.hand.filter(c => c.id !== card.id && !s.pending?.receivedIds?.includes(c.id)).sort((a, b) => rankWeight(a.rank) - rankWeight(b.rank))[0] : undefined;
  return act(s, player.id, { ...base, type: 'play', moves: [selected], ...(gift ? { giftIds: [gift.id] } : {}), uhOh: true });
}
